/**
 * MyJobProPanel — v49.5. PRO panel in Destine Ads > MyJob:
 *   * Create / delete offers (5 free/month, then €10 Stripe pack for +5)
 *   * Applications inbox: view CV & letter (open/download), accept, reject, delete
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Briefcase, Plus, Trash2, X, Save, Loader2, FileText, Check, XCircle, Users, Download, Building2, MapPin, DollarSign, CreditCard, User } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { getMediaUrl } from '../../utils/mediaUrl';
import { JOB_L, CONTRACT_TYPES, CURRENCIES, money, contractLabel } from './i18n';
import MyJobEmployees from './MyJobEmployees';
import { Link } from 'react-router-dom';
import { PortfolioView } from '../portfolio/MyPortfolio';
import { portfolioTexts } from '../portfolio/portfolioTexts';

const MyJobProPanel = () => {
  const { language } = useLanguage();
  const l = JOB_L[language] || JOB_L.fr;
  const PL = portfolioTexts(language);
  const [tab, setTab] = useState('offers'); // offers | applications | employees
  const [offers, setOffers] = useState([]);
  const [apps, setApps] = useState([]);
  const [quota, setQuota] = useState({ left: 5, free: 5, extra: 0, pack_price_eur: 10 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: '', salary_amount: 0, salary_currency: 'EUR', position: '', contract_type: 'CDI', location: '', company_name: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, a, q] = await Promise.all([
        axios.get('/api/myjob/pro/offers'),
        axios.get('/api/myjob/pro/applications'),
        axios.get('/api/myjob/pro/quota'),
      ]);
      setOffers(o.data.items || []);
      setApps(a.data.items || []);
      setQuota(q.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Detect Stripe success callback in URL and poll confirm.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sref = params.get('myjob_paid');
    if (!sref) return;
    (async () => {
      try {
        const r = await axios.post('/api/myjob/pro/quota/confirm', { session_ref: sref });
        if (r.data.paid || r.data.already_active) {
          toast.success(l.paymentSuccess);
          load();
        }
      } catch (_) { /* silent */ }
      const url = new URL(window.location.href);
      url.searchParams.delete('myjob_paid');
      window.history.replaceState({}, '', url.toString());
    })();
  }, [l.paymentSuccess, load]);

  const create = async () => {
    if (!form.title.trim() || !form.description.trim()) { toast.error('Titre et description requis'); return; }
    setCreating(true);
    try {
      // v49.8 — Send X-App-Language so backend tags the offer with the current app language.
      await axios.post('/api/myjob/pro/offers', form, {
        headers: { 'X-App-Language': language || 'fr' },
      });
      toast.success('OK');
      setForm({ title: '', description: '', category: '', salary_amount: 0, salary_currency: 'EUR', position: '', contract_type: 'CDI', location: '', company_name: '' });
      load();
    } catch (e) {
      if (e?.response?.status === 402) { toast.error(l.quotaFull); }
      else { toast.error(e?.response?.data?.detail || 'Error'); }
    } finally { setCreating(false); }
  };

  const buyPack = async () => {
    try {
      const r = await axios.post('/api/myjob/pro/quota/checkout');
      const url = r.data.checkout_url;
      const sid = r.data.session_id;
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url, presentationStyle: 'popover' });
        let attempts = 0;
        const iv = setInterval(async () => {
          attempts += 1;
          try {
            const c = await axios.post('/api/myjob/pro/quota/confirm', { session_id: sid });
            if (c.data.paid || c.data.already_active) {
              clearInterval(iv);
              try { await Browser.close(); } catch (_) { /* noop */ }
              toast.success(l.paymentSuccess);
              load();
            }
          } catch (_) { /* poll */ }
          if (attempts > 100) clearInterval(iv);
        }, 3000);
      } else {
        window.location.href = url;
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const deleteOffer = async (id) => {
    if (!window.confirm(l.confirmDelete)) return;
    try { await axios.delete(`/api/myjob/pro/offers/${id}`); toast.success('OK'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const decideApp = async (id, action) => {
    try { await axios.post(`/api/myjob/pro/applications/${id}/${action}`); toast.success('OK'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  const deleteApp = async (id) => {
    if (!window.confirm(l.confirmDelete)) return;
    try { await axios.delete(`/api/myjob/pro/applications/${id}`); toast.success('OK'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-4" data-testid="myjob-pro-panel">
      {/* Header + quota */}
      <div className="p-4 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-600/10 border border-amber-500/30 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2"><Briefcase className="w-5 h-5 text-amber-300" /> {l.title}</h3>
          <p className="text-xs text-white/70">{l.quotaBanner} — {quota.used || 0} / {quota.free + (quota.extra || 0)} · <strong>{quota.left} {l.quotaLeft}</strong></p>
        </div>
        {quota.left <= 0 && (
          <button onClick={buyPack} className="px-3 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-black text-xs font-bold flex items-center gap-1.5" data-testid="myjob-buy-pack-btn"><CreditCard className="w-3.5 h-3.5" /> {l.buyPack}</button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button onClick={() => setTab('offers')} className={`flex-1 px-3 py-2 rounded-full text-xs font-semibold ${tab === 'offers' ? 'bg-amber-500 text-black' : 'bg-white/5 text-gray-300'}`} data-testid="myjob-tab-offers">{l.myOffers} ({offers.length})</button>
        <button onClick={() => setTab('applications')} className={`flex-1 px-3 py-2 rounded-full text-xs font-semibold ${tab === 'applications' ? 'bg-amber-500 text-black' : 'bg-white/5 text-gray-300'}`} data-testid="myjob-tab-apps">{l.applications} ({apps.length})</button>
        <button onClick={() => setTab('employees')} className={`flex-1 px-3 py-2 rounded-full text-xs font-semibold ${tab === 'employees' ? 'bg-amber-500 text-black' : 'bg-white/5 text-gray-300'}`} data-testid="myjob-tab-employees"><Users className="w-3.5 h-3.5 inline mr-1" />{{ fr:'Mes employés', en:'My employees', es:'Mis empleados', pt:'Meus empregados', de:'Meine Angestellten', it:'I miei dipendenti', ru:'Мои сотрудники', ar:'موظفوني', zh:'我的员工', ja:'従業員', hi:'मेरे कर्मचारी', nl:'Mijn werknemers', sw:'Wafanyakazi wangu', ht:'Anplwaye m yo' }[language] || 'Mes employés'}</button>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-300" /></div>
      ) : tab === 'offers' ? (
        <>
          {/* Create form */}
          {quota.left > 0 && (
            <section className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
              <h4 className="text-sm font-bold flex items-center gap-2"><Plus className="w-4 h-4" /> {l.newOffer}</h4>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder={l.title_ph} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="myjob-form-title" />
              <textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder={l.description_ph} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="myjob-form-desc" />
              <div className="grid grid-cols-2 gap-2">
                <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder={l.category_ph} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                <input value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} placeholder={l.position_ph} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                <input type="number" min="0" step="0.01" value={form.salary_amount} onChange={e => setForm({ ...form, salary_amount: parseFloat(e.target.value) || 0 })} placeholder={l.salary_ph} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                <select value={form.salary_currency} onChange={e => setForm({ ...form, salary_currency: e.target.value })} className="bg-white/10 border border-white/15 rounded-lg px-2 py-2 text-sm">
                  {CURRENCIES.map(c => <option key={c} value={c} className="bg-gray-900">{c}</option>)}
                </select>
                <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder={l.location_ph} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                <input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} placeholder={l.company_ph} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                <select value={form.contract_type} onChange={e => setForm({ ...form, contract_type: e.target.value })} className="col-span-2 bg-white/10 border border-white/15 rounded-lg px-2 py-2 text-sm" data-testid="myjob-form-contract">
                  {CONTRACT_TYPES.map(c => <option key={c} value={c} className="bg-gray-900">{contractLabel(c, language)}</option>)}
                </select>
              </div>
              <button onClick={create} disabled={creating} className="w-full py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-black font-semibold disabled:opacity-50 flex items-center justify-center gap-2" data-testid="myjob-form-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {creating ? l.saving : l.save}
              </button>
            </section>
          )}

          {/* Offers list */}
          {offers.length === 0 ? (
            <p className="text-sm text-white/40 italic text-center py-4">{l.empty}</p>
          ) : (
            <div className="space-y-2">
              {offers.map(o => (
                <div key={o.id} className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3" data-testid={`myjob-offer-row-${o.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{o.title}</p>
                    <p className="text-xs text-white/60 flex items-center gap-2 flex-wrap">
                      <span>{contractLabel(o.contract_type, language)}</span>
                      {o.company_name && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {o.company_name}</span>}
                      {o.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {o.location}</span>}
                      {o.salary_amount > 0 && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> {money(o.salary_amount, o.salary_currency)}</span>}
                    </p>
                    <p className="text-[10px] text-white/40 mt-1">{o.applications_count || 0} {l.applications.toLowerCase()}</p>
                  </div>
                  <button onClick={() => deleteOffer(o.id)} className="p-2 rounded-lg hover:bg-red-500/20 text-red-300" data-testid={`myjob-offer-del-${o.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </>
      ) : tab === 'employees' ? (
        <MyJobEmployees />
      ) : (
        <>
          {apps.length === 0 ? (
            <p className="text-sm text-white/40 italic text-center py-4">{l.empty}</p>
          ) : (
            <div className="space-y-2">
              {apps.map(a => (
                <div key={a.id} className="p-3 rounded-2xl bg-white/5 border border-white/10 space-y-2" data-testid={`myjob-app-row-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{a.applicant_name} <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${a.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-300' : a.status === 'rejected' ? 'bg-red-500/20 text-red-300' : 'bg-yellow-500/20 text-yellow-300'}`}>{a.status === 'accepted' ? l.accepted : a.status === 'rejected' ? l.rejected : l.pending}</span></p>
                      <p className="text-xs text-cyan-300 break-all">{a.applicant_email}</p>
                      <p className="text-[10px] text-white/40">→ {a.offer_title}</p>
                      {a.message && <p className="text-xs text-white/60 italic mt-1">« {a.message} »</p>}
                    </div>
                  </div>
                  {a.portfolio && (
                    <details className="mb-2 rounded-xl bg-fuchsia-500/10 border border-fuchsia-500/30 p-2" data-testid={`myjob-app-portfolio-${a.id}`}>
                      <summary className="text-xs font-semibold text-fuchsia-200 cursor-pointer">💼 {PL.sentPortfolio}{a.portfolio.headline ? ` — ${a.portfolio.headline}` : ''}</summary>
                      <div className="mt-2"><PortfolioView data={a.portfolio} L={PL} /></div>
                    </details>
                  )}
                  <div className="flex gap-1.5 flex-wrap">
                    {a.profile_url && <Link to={a.profile_url} className="px-2 py-1 rounded-lg bg-fuchsia-500/20 text-fuchsia-200 text-xs flex items-center gap-1 hover:bg-fuchsia-500/30" data-testid={`myjob-app-profile-${a.id}`}><User className="w-3 h-3" /> {PL.viewProfile}</Link>}
                    {a.cv_url && <a href={getMediaUrl(a.cv_url)} target="_blank" rel="noreferrer" className="px-2 py-1 rounded-lg bg-blue-500/20 text-blue-200 text-xs flex items-center gap-1 hover:bg-blue-500/30" data-testid={`myjob-app-cv-${a.id}`}><Download className="w-3 h-3" /> {l.downloadCV}</a>}
                    {a.letter_url && <a href={getMediaUrl(a.letter_url)} target="_blank" rel="noreferrer" className="px-2 py-1 rounded-lg bg-purple-500/20 text-purple-200 text-xs flex items-center gap-1 hover:bg-purple-500/30" data-testid={`myjob-app-letter-${a.id}`}><Download className="w-3 h-3" /> {l.downloadLetter}</a>}
                    {a.status === 'pending' && (
                      <>
                        <button onClick={() => decideApp(a.id, 'accept')} className="px-2 py-1 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-xs flex items-center gap-1" data-testid={`myjob-app-accept-${a.id}`}><Check className="w-3 h-3" /> {l.accept}</button>
                        <button onClick={() => decideApp(a.id, 'reject')} className="px-2 py-1 rounded-lg bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 text-xs flex items-center gap-1" data-testid={`myjob-app-reject-${a.id}`}><XCircle className="w-3 h-3" /> {l.reject}</button>
                      </>
                    )}
                    <button onClick={() => deleteApp(a.id)} className="px-2 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-xs flex items-center gap-1" data-testid={`myjob-app-del-${a.id}`}><Trash2 className="w-3 h-3" /> {l.delete}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyJobProPanel;
